import React from 'react';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import FormPartial from '../../components/form/formContainer/FormPartial';
import TextInput from '../../components/form/formInputs/TextInput/TextInput';
import useFixedAssetDepreciationMethodTypeForm from './hooks/useFixedAssetDepreciationMethodTypeForm';
import NumericInput from '../../components/form/formInputs/NumericInput/NumericInput';
import BaseForm, { ParamTypes } from '../../components/form/baseForm/BaseForm';
import { NumberMaxValues, RoutingValues } from '../../types/Shared.types';
import { DEPRECIATION_METHOD_TYPE_FIELDS } from '../../constants/revisionHistory/revisionHistory.constants';
import { DepreciationMethodTypesFormDefs } from '../../constants/i18n/translations/termDefinitions/accounting';
import TranslatableText from '../../components/i18n/TranslatableText';

const FixedAssetDepreciationMethodTypeForm = () => {
    const { id } = useParams<ParamTypes>();
    const {
        fields,
        handleFieldChange,
        formMethods,
        onCreate,
        onUpdate,
        onDelete,
        closeForm,
        isLoadingType,
        canCreateDepreciationMethodType,
        canUpdateDepreciationMethodType,
        canDeleteDepreciationMethodType,
        termSet,
    } = useFixedAssetDepreciationMethodTypeForm(id);

    return (
        <BaseForm
            title={
                <TranslatableText
                    termSet={termSet}
                    termKey={
                        DepreciationMethodTypesFormDefs.Fixed_Asset_Depreciation_Method_Type
                    }
                />
            }
            formMethods={formMethods}
            isLoading={isLoadingType}
            isNew={id === RoutingValues.newId}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onCancel={closeForm}
            showOnRevision={true}
            fieldMappings={DEPRECIATION_METHOD_TYPE_FIELDS}
            entityUuid={fields?.uuid}
            displayCreateButton={canCreateDepreciationMethodType}
            displayUpdateButton={canUpdateDepreciationMethodType}
            displayDeleteButton={canDeleteDepreciationMethodType}>
            <FormPartial>
                <Grid container flexDirection="row" spacing={2}>
                    <Grid item xs={6}>
                        <TextInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={
                                        DepreciationMethodTypesFormDefs.Depreciation_Method_Type_Code
                                    }
                                />
                            }
                            id="code"
                            value={fields.code}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            maxLength={30}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <NumericInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={
                                        DepreciationMethodTypesFormDefs.Sort_Order
                                    }
                                />
                            }
                            id="sortOrder"
                            value={fields.sortOrder}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            maxValue={NumberMaxValues.smallint}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={
                                        DepreciationMethodTypesFormDefs.Description
                                    }
                                />
                            }
                            id="description"
                            value={fields.description}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            maxLength={255}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControlLabel
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={
                                        DepreciationMethodTypesFormDefs.Journal_Posting
                                    }
                                />
                            }
                            control={
                                <Checkbox
                                    id="postsJournalEntries"
                                    checked={fields.postsJournalEntries}
                                    onChange={handleFieldChange}
                                />
                            }
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControlLabel
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={
                                        DepreciationMethodTypesFormDefs.Predefined_Schedule
                                    }
                                />
                            }
                            control={
                                <Checkbox
                                    id="predefinedSchedule"
                                    checked={fields.predefinedSchedule}
                                    onChange={handleFieldChange}
                                />
                            }
                        />
                    </Grid>
                </Grid>
            </FormPartial>
        </BaseForm>
    );
};

export default FixedAssetDepreciationMethodTypeForm;
